import { useState, useEffect, useRef } from 'preact/hooks';
import { SubscriptionsListSkeleton } from './LoadingSkeletons';
import { t } from '../i18n';
import { getConfig } from '../config';
import { getNewsLists, updateSubscription, type NewsListItem } from '../api';
import { newsListField } from '../utils/newsListI18n';
import { store } from '../store';
import { getAntiBotEnabled, getAntiBotMinFillMs } from '../antiBot';

export function SubscriptionsPage() {
  const config = getConfig();
  const formStartedAtRef = useRef(Date.now());
  const auth = store.getAuth();
  const email = (auth?.customer_email ?? (auth as any)?.email ?? '').trim();
  const accountLabel = (email || auth?.login || '').trim();

  const [lists, setLists] = useState<NewsListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [swHoney, setSwHoney] = useState('');
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!email) {
      setLoading(false);
      return;
    }
    getNewsLists(config, email).then((res) => {
      setLoading(false);
      if (res.status === 200 && res.result) {
        const arr = Array.isArray(res.result) ? res.result : [];
        setLists(arr);
        const map: Record<number, boolean> = {};
        arr.forEach((l) => { map[l.listid] = l.subscribed === 'Y'; });
        setChecked(map);
      }
    });
  }, [email]);

  const handleToggle = (listid: number) => {
    setChecked((prev) => ({ ...prev, [listid]: !prev[listid] }));
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!email || saving) return;
    if (getAntiBotEnabled() && (swHoney.trim() !== '' || Date.now() - formStartedAtRef.current < getAntiBotMinFillMs())) {
      setMessage({ type: 'error', text: t('subs.failed') });
      return;
    }
    setSaving(true);
    setMessage(null);
    const newsListIds: Record<string, string> = {};
    Object.entries(checked).forEach(([id, value]) => {
      if (value) newsListIds[id] = 'on';
    });
    const res = await updateSubscription(config, {
      email,
      newsListIds,
      sw_honey: swHoney,
      sw_form_ts: formStartedAtRef.current,
    });
    setSaving(false);
    if (res.status === 200) {
      setMessage({ type: 'success', text: t('subs.success') });
      store.addToast(t('subs.success'), 'success');
    } else {
      setMessage({ type: 'error', text: (res.error as string) || t('subs.failed') });
    }
  };

  if (loading) {
    return <SubscriptionsListSkeleton />;
  }

  if (!email) {
    return (
      <div
        class="d-flex flex-column align-items-center justify-content-center w-100 py-5 px-3"
        style={{ minHeight: 'min(50vh, 440px)', boxSizing: 'border-box' }}
      >
        <div class="sw-surface-card p-4 text-center" style={{ maxWidth: '440px', width: '100%' }}>
          <p class="mb-0 text-muted">{t('subs.loginRequired')}</p>
        </div>
      </div>
    );
  }

  if (!lists || lists.length === 0) {
    return (
      <div class="shop-widget-subscriptions">
        <div class="sw-surface-card p-3 p-md-4">
          {accountLabel && (
            <div class="d-flex justify-content-end mb-2">
              <span
                class="badge border"
                style={{
                  color: 'var(--sw-text)',
                  backgroundColor: 'color-mix(in srgb, var(--sw-bg) 88%, var(--sw-border) 12%)',
                  borderColor: 'var(--sw-border)',
                }}
              >
                {accountLabel}
              </span>
            </div>
          )}
          <p class="text-muted mb-0">{t('subs.empty')}</p>
        </div>
      </div>
    );
  }

  return (
    <div class="shop-widget-subscriptions">
      <div class="sw-surface-card p-3 p-md-4">
        {accountLabel && (
          <div class="d-flex justify-content-end mb-2">
            <span
              class="badge border"
              style={{
                color: 'var(--sw-text)',
                backgroundColor: 'color-mix(in srgb, var(--sw-bg) 88%, var(--sw-border) 12%)',
                borderColor: 'var(--sw-border)',
              }}
            >
              {accountLabel}
            </span>
          </div>
        )}
        <p class="text-muted small mb-3">{t('subs.instruction')}</p>

        <form onSubmit={handleSubmit}>
        <input
          type="text"
          name="sw_honey"
          value={swHoney}
          onInput={(e) => setSwHoney((e.target as HTMLInputElement).value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
        />
        {lists.map((list) => (
          <div key={list.listid} class="border rounded-3 p-3 mb-2 bg-white">
            <div class="form-check form-switch m-0">
              <input
                class="form-check-input"
                type="checkbox"
                id={`sub-${list.listid}`}
                checked={checked[list.listid] ?? false}
                onChange={() => handleToggle(list.listid)}
              />
              <label class="form-check-label fw-semibold" for={`sub-${list.listid}`}>
                {newsListField(list, 'name')}
              </label>
            </div>
            {list.descr && (
              <div class="text-muted small mt-2 ms-4">{newsListField(list, 'descr')}</div>
            )}
          </div>
        ))}

        {message && (
          <div class={`alert alert-${message.type === 'success' ? 'success' : 'danger'} py-2 mb-3`} role="alert">
            {message.text}
          </div>
        )}

        <div class="d-flex justify-content-end mt-3">
          <button type="submit" class="btn btn-primary px-4" disabled={saving}>
            {saving ? (
              <>
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                {t('subs.updating')}
              </>
            ) : (
              t('subs.update')
            )}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
}
